import Vue from "vue";
import { Component, Provide, Watch } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { WindowType } from "../../../util";
import { Card, CardAttributeDefinitionType, ListItem } from "../../backend/backendapi";
import OrganigramModal from "../../organigramModal/organigramModal.controller";
import { Operator, SearchEvent, Suggestion } from "../../search/search.model";
import { KanbanState } from "../kanban.model";
import SignalR from "../SignalR";
import UserSettingsButton from '../settings/UserSettingsButton';
import KanbanList from '../KanbanList';

Vue.use(Toasted, { duration: 2000 });

declare var window: WindowType;

@Component({
    name: 'KanbanSite',
    components: {
        KanbanList,
        SignalR,
        Search: require('../../search/search.vue.html').default,
        OrganigramModal: require('../../organigramModal/organigramModal.vue.html').default,
        UserLimit: require('../../userlimit/userlimit.vue.html').default,
        UserSettingsButton,
    },
})
export default class KanbanSite extends Vue {
    @Provide()
    private state = new KanbanState();

    async mounted(): Promise<void> {
        await this.reloadAll();
    }

    private async reloadAll(): Promise<void> {
        await this.state.reloadAll(this);
        await (this.$refs.organigramModal as OrganigramModal).reloadAll();
        this.$root.$emit('reloaded');
    }

    private async saveSettings(): Promise<void> {
        await this.state.settingsAPI.update("KanbanSettings", false, false, JSON.stringify(this.state.settings));
    }

    //watches
    @Watch('state.accounts.selectedItem')
    private accountSelected(newValue: number, oldValue: number | undefined): void {
        if (oldValue !== undefined) { // do not reload then the value is initialized on load
            this.reloadAll();
        }
    }

    @Watch('state.archived')
    private onArchiveChanged(): void {
        this.reloadAll();
    }
    
    @Watch('state.settings.alwaysShowMyBoards')
    private onAlwaysShowMyBoardsChanged(newValue: boolean, oldValue: boolean | undefined): void {
        if (oldValue !== undefined) { // do not reload then the value is initialized on load
            this.reloadAll();
        }
    }

    //events
    private onSearch(event: SearchEvent<any, any, Card>): void {
        let predicates = event.segments
            .filter(s => s.operator && s.value.trim().length)
            .map(s => (s.suggestion as Suggestion<any, any, Card>).buildPredicate(s.operator!, s.value));
        if (event.search.trim().length) {
            predicates.push((c: Card) =>
                (c.name ?? '').toLocaleLowerCase().includes(event.search.toLocaleLowerCase())
                || (c.description ?? '').toLocaleLowerCase().includes(event.search.toLocaleLowerCase()));
        }
        let predicate = (c: Card) => true as boolean;
        if (predicates.length) {
            predicate = predicates.reduce((l, r) => (c: Card) => l(c) && r(c), (c: Card) => true as boolean);
        }
        this.state.filter = predicate;
    }

    private onAccountSelected(id: number): void {
        if (this.state.accounts) {
            this.state.accounts.selectedItem = id; // triggers reload via watch on function 'accountSelected'
        } else {
            this.reloadAll();
        }
    }

    //getter
    private get suggestions(): Suggestion<any, any, Card>[] {
        return [
            new Suggestion('responsible', this.$t('kanbanResponsibles').toString(), [
                new Operator('eq', '=', (ex: ListItem[], ac: string) => ex.map(e => e.email!.toLocaleLowerCase()).includes(ac.toLocaleLowerCase())),
                new Operator('ne', '≠', (ex: ListItem[], ac: string) => !ex.map(e => e.email!.toLocaleLowerCase()).includes(ac.toLocaleLowerCase())),
            ], this.state.users.map(e => e.email!), (c: Card) => {
                return c.responsibles?.map(e => this.state.users.find(u => u.id === e.userId)).filter(e => e).map(e => e!) ?? [];
            }),
            new Suggestion('description', this.$t('kanbanDescription').toString(), [
                new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
                new Operator('ne', '≠', (ex: string, ac: string) => !ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
            ], [], (c: Card) => c.description ?? ''),
            new Suggestion('title', this.$t('kanbanTitle').toString(), [
                new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
                new Operator('ne', '≠', (ex: string, ac: string) => !ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
            ], [], (c: Card) => c.name),
            ...(this.state.selectedBoard
                    ? this.state.selectedBoard.attributeDefinitions?.filter(def => def.type === CardAttributeDefinitionType.String || def.type === CardAttributeDefinitionType.Integer)
                    .map(def => {
                        return new Suggestion(def.name, def.name, [
                                new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
                                new Operator('ne', '≠', (ex: string, ac: string) => !ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
                            ], (this.state.selectedBoard?.tickets?.map(t => t.cards ?? []).reduce((l, r) => l.concat(r), []) ?? [])
                                .map(c => c.attributes?.filter(attr => attr.definitionId === def.id) ?? []).reduce((l, r) => l.concat(r), [])
                                .map(a => a.value)
                                .filter((value, index, self) => self.indexOf(value) === index), //unique filter
                            (c: Card) => c.attributes?.find(attr => attr.definitionId === def.id)?.value ?? "");
                    }) ?? []
                    : []
            ),
        ];
    }

    private get baseurl(): string {
        return window.application.baseUrl;
    }

    private get userid(): string | undefined {
        return window.application.kanban.user?.id;
    }

    private get username(): string | undefined {
        return window.application.kanban.user?.userName;
    }

    private get isFireProtection(): boolean {
        return window.application.permissions.misc.fireProtection;
    }
    
    private get canSeeAssignedCards(): boolean {
        return this.state.config?.elementKeysToShow.includes("app.ui.config.actionsmenu") ?? false;
    }
}
